<!-- Hero -->
<div class="dashboard-hero">
  <div class="hero-content">
    <h1 class="hero-title">
      <i class="bi bi-journals hero-icon"></i> Articles Management
    </h1>
    <p class="hero-subtitle">Manage and configure article definitions and their properties.</p>
  </div>
</div>

<div class="container dashboard-container">

  <!-- Error -->
  <div class="alert alert-warning alert-dismissible" role="alert" *ngIf="displayError">
    <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ errorMessage }}
  </div>

  <div class="modern-card mb-4">
    <div class="modern-card-header">
      <div class="card-title-wrapper">
        <i class="bi bi-journals card-icon"></i>
        <h5 class="modern-card-title">Articles</h5>
      </div>
      <div class="header-actions">
        <button type="button" class="btn-modern-action btn-action-secondary"
          (click)="navigateToArticleStaging()">
          <i class="bi bi-upload"></i> Article Staging
        </button>
        <button type="button" class="btn-modern-action btn-action-primary"
          (click)="openCreateArticleModal(createArticleModal)">
          <i class="bi bi-plus-lg"></i> Create Article
        </button>
      </div>
    </div>

    <div class="modern-card-body">
      <form class="article-search-wrapper">

        <!-- Row 1: search + clear + system filter -->
        <div class="search-filter-row">
          <div class="search-field-wrapper">
            <label for="table-filtering-search" class="article-search-label">
              <i class="bi bi-search"></i> Full text search:
            </label>
            <input id="table-filtering-search" placeholder="Search articles…"
              class="form-control search-input" type="text" name="searchTerm"
              [(ngModel)]="searchTerm"
              (keydown.enter)="search($event)"
              (input)="applyFilters()" />
          </div>

          <div class="search-field-wrapper">
            <label for="systemFilter" class="article-search-label">
              <i class="bi bi-diagram-3"></i> Filter by System:
            </label>
            <select class="form-select search-input" id="systemFilter"
              [ngModel]="selectedSystem"
              (ngModelChange)="onSystemFilterChange($event)"
              name="systemFilter">
              <option value="">All Systems</option>
              <option *ngFor="let system of systems" [value]="system.name">{{ system.name }}</option>
            </select>
          </div>

          <div class="filter-field-wrapper">
            <label class="article-search-label"><i class="bi bi-funnel-fill"></i> Filter:</label>
            <div class="filter-controls">
              <span ngbDropdown (keydown.enter)="$event.preventDefault()">
                <button class="btn btn-filter-modern" ngbDropdownToggle
                  (keydown.enter)="$event.preventDefault()">
                  <i class="bi bi-funnel-fill"></i> Filter
                </button>
                <ul ngbDropdownMenu (keydown.enter)="$event.preventDefault()">
                  <li class="dropdown-item" *ngFor="let filter of filterOptions">
                    <input type="checkbox" [checked]="filter.selected"
                      (change)="onFilterChange(filter, $event)" />
                    {{ filter.label }}
                  </li>
                </ul>
              </span>
              <button type="button" class="btn-modern-action btn-action-secondary"
                (click)="clearAllFilters()">
                <i class="bi bi-x-circle"></i> Clear Filters
              </button>
              <span class="d-flex flex-wrap gap-1">
                <span class="filter-tag" *ngFor="let filter of getSelectedFilters()">
                  {{ filter.label }}
                  <span (click)="removeFilter(filter)" aria-label="Close" class="filter-tag-close">&#x2715;</span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <div *ngIf="loading" class="loader-overlay">
          <div class="spinner-border text-primary"></div>
        </div>

        <!-- Articles table -->
        <div class="mgmt-table-scroll-wrapper mt-3" *ngIf="filteredArticles.length">
          <table class="mgmt-table">
            <thead>
              <tr>
                <th scope="col" (click)="onSort('articleName')">
                  Article Name <i class="bi ms-1" [ngClass]="getSortIcon('articleName')"></i>
                </th>
                <th scope="col" (click)="onSort('material')">
                  Material <i class="bi ms-1" [ngClass]="getSortIcon('material')"></i>
                </th>
                <th scope="col" (click)="onSort('type')">
                  Type <i class="bi ms-1" [ngClass]="getSortIcon('type')"></i>
                </th>
                <th scope="col" (click)="onSort('system')">
                  System <i class="bi ms-1" [ngClass]="getSortIcon('system')"></i>
                </th>
                <th scope="col" (click)="onSort('updatedAt')">
                  Last Update <i class="bi ms-1" [ngClass]="getSortIcon('updatedAt')"></i>
                </th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let article of filteredArticles">
                <td>{{ article.articleName }}</td>
                <td>{{ article.material }}</td>
                <td>{{ article.type }}</td>
                <td>
                  <span class="system-badge">{{ getSystemNameForArticle(article.articleName) }}</span>
                </td>
                <td>{{ formatDate(article.updatedAt) }}</td>
                <td>
                  <div class="row-actions">
                    <button type="button" class="btn-modern-action btn-action-primary"
                      (click)="openArticleDetailModal(articleDetailModal, article)">
                      <i class="bi bi-eye"></i> View
                    </button>
                    <button type="button" class="btn-modern-action btn-action-secondary"
                      (click)="navigateToArticleViewer(article)" title="View 3D Model">
                      <i class="bi bi-box"></i> Model
                    </button>
                    <button type="button" class="btn-modern-action btn-action-secondary"
                      (click)="openEditArticleModal(editArticleModal, article)">
                      <i class="bi bi-pencil"></i> Edit
                    </button>
                    <button type="button" class="btn-icon-danger"
                      (click)="deleteArticle(article)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div *ngIf="!filteredArticles.length && !loading" class="mgmt-empty-state">
          <i class="bi bi-inbox"></i>
          <p>No articles found.</p>
        </div>

        <!-- Pagination info -->
        <div *ngIf="totalItems > 0" class="pagination-info">
          <span>Showing {{ getItemRangeStart() }}–{{ getItemRangeEnd() }} of {{ totalItems }} articles</span>
          <span>Page {{ currentPage }} of {{ getTotalPages() }}</span>
        </div>

        <!-- Pagination -->
        <nav *ngIf="totalItems > itemsPerPage" aria-label="Articles pagination" class="mgmt-pagination">
          <ul class="pagination justify-content-center">
            <li class="page-item" [class.disabled]="currentPage === 1">
              <a class="page-link" (click)="currentPage !== 1 ? onPageChange(currentPage - 1, $event) : null"
                href="#" role="button"
                [style.pointer-events]="currentPage === 1 ? 'none' : 'auto'">Previous</a>
            </li>
            <li class="page-item" *ngIf="getPagesArray()[0] > 1">
              <a class="page-link" (click)="onPageChange(1, $event)" href="#" role="button">1</a>
            </li>
            <li class="page-item disabled" *ngIf="getPagesArray()[0] > 2">
              <span class="page-link">...</span>
            </li>
            <li class="page-item" *ngFor="let page of getPagesArray()" [class.active]="currentPage === page">
              <a class="page-link" (click)="onPageChange(page, $event)" href="#" role="button">{{ page }}</a>
            </li>
            <li class="page-item disabled"
              *ngIf="getPagesArray()[getPagesArray().length - 1] < getTotalPages() - 1">
              <span class="page-link">...</span>
            </li>
            <li class="page-item"
              *ngIf="getPagesArray()[getPagesArray().length - 1] < getTotalPages()">
              <a class="page-link" (click)="onPageChange(getTotalPages(), $event)" href="#" role="button">{{ getTotalPages() }}</a>
            </li>
            <li class="page-item" [class.disabled]="currentPage === getTotalPages()">
              <a class="page-link" (click)="currentPage !== getTotalPages() ? onPageChange(currentPage + 1, $event) : null"
                href="#" role="button"
                [style.pointer-events]="currentPage === getTotalPages() ? 'none' : 'auto'">Next</a>
            </li>
          </ul>
        </nav>

      </form>
    </div>
  </div>
</div>

<!-- Article Detail Modal -->
<ng-template #articleDetailModal let-modal>
  <div class="modal-header detail-modal-header">
    <div class="detail-modal-title-wrapper">
      <div class="detail-modal-icon">
        <i class="bi bi-journal-text"></i>
      </div>
      <div>
        <h4 class="modal-title detail-modal-title" id="modal-basic-title">
          {{ modalContent?.articleName }}
        </h4>
        <p class="detail-modal-subtitle">Article Details</p>
      </div>
    </div>
    <button type="button" class="btn-close btn-close-white" aria-label="Close"
      (click)="modal.dismiss('Cross click')"></button>
  </div>
  <div class="modal-body detail-modal-body">
    <div class="detail-info-grid">
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-tag-fill me-2"></i>Article Name</div>
        <div class="detail-info-value">{{ modalContent?.articleName }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-layers-fill me-2"></i>Type</div>
        <div class="detail-info-value">{{ modalContent?.type || '—' }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-gem me-2"></i>Material</div>
        <div class="detail-info-value">{{ modalContent?.material || '—' }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-diagram-3-fill me-2"></i>System</div>
        <div class="detail-info-value">
          <span class="system-badge">{{ getSystemNameForArticle(modalContent?.articleName || '') }}</span>
        </div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-calendar-plus-fill me-2"></i>Created</div>
        <div class="detail-info-value">{{ formatDate(modalContent?.createdAt) || '—' }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-calendar-check-fill me-2"></i>Updated</div>
        <div class="detail-info-value">{{ formatDate(modalContent?.updatedAt) || '—' }}</div>
      </div>
    </div>
  </div>
  <div class="modal-footer detail-modal-footer">
    <button type="button" class="btn-modern-action btn-action-secondary"
      (click)="modal.dismiss('Close click')">
      <i class="bi bi-x-lg"></i> Close
    </button>
  </div>
</ng-template>

<!-- Create/Edit Article Modal -->
<!-- Create Article Modal -->
<ng-template #createArticleModal let-modal>
  <div class="modal-header detail-modal-header">
    <div class="detail-modal-title-wrapper">
      <div class="detail-modal-icon">
        <i class="bi bi-plus-lg"></i>
      </div>
      <div>
        <h4 class="modal-title detail-modal-title" id="modal-basic-title">Create Article</h4>
        <p class="detail-modal-subtitle">Add a new article definition</p>
      </div>
    </div>
    <button type="button" class="btn-close btn-close-white" aria-label="Close"
      (click)="modal.dismiss('Cross click')"></button>
  </div>
  <div class="modal-body form-modal-body">
    <form #articleForm="ngForm">
      <div class="form-field-group">
        <label for="articleName" class="form-modal-label">
          <i class="bi bi-tag-fill"></i> Article Name <span class="required-star">*</span>
        </label>
        <input type="text" class="form-control form-modal-input" id="articleName"
          name="articleName" [(ngModel)]="modalContent!.articleName"
          placeholder="Enter article name" required />
      </div>
      <div class="form-field-group">
        <label for="articleType" class="form-modal-label">
          <i class="bi bi-layers-fill"></i> Type <span class="required-star">*</span>
        </label>
        <input type="text" class="form-control form-modal-input" id="articleType"
          name="type" [(ngModel)]="modalContent!.type"
          placeholder="Enter article type" required />
      </div>
      <div class="form-field-group">
        <label for="articleMaterial" class="form-modal-label">
          <i class="bi bi-gem"></i> Material
        </label>
        <select class="form-select form-modal-input" id="articleMaterial"
          name="material" [(ngModel)]="modalContent!.material">
          <option value="">Select Material</option>
          <option value="Aluminum">Aluminum</option>
          <option value="Steel">Steel</option>
          <option value="Composite">Composite</option>
        </select>
      </div>
      <div class="form-field-group">
        <label for="articleSystem" class="form-modal-label">
          <i class="bi bi-diagram-3-fill"></i> Assign to System
        </label>
        <select class="form-select form-modal-input" id="articleSystem"
          name="system" [(ngModel)]="selectedSystemForArticle">
          <option value="">Select System (Optional)</option>
          <option *ngFor="let system of systems" [value]="system.id">{{ system.name }}</option>
        </select>
      </div>
    </form>
  </div>
  <div class="modal-footer detail-modal-footer">
    <button type="button" class="btn-modern-action btn-action-secondary"
      (click)="modal.dismiss('Cancel click')">
      <i class="bi bi-x-lg"></i> Cancel
    </button>
    <button type="button" class="btn-modern-action btn-action-primary"
      (click)="saveArticle(modalContent!)"
      [disabled]="!modalContent!.type || !modalContent!.articleName">
      <i class="bi bi-check-lg"></i> Save Article
    </button>
  </div>
</ng-template>

<!-- Edit Article Modal -->
<ng-template #editArticleModal let-modal>
  <div class="modal-header detail-modal-header">
    <div class="detail-modal-title-wrapper">
      <div class="detail-modal-icon">
        <i class="bi bi-pencil-fill"></i>
      </div>
      <div>
        <h4 class="modal-title detail-modal-title" id="modal-basic-title">Edit Article</h4>
        <p class="detail-modal-subtitle">{{ modalContent?.articleName }}</p>
      </div>
    </div>
    <button type="button" class="btn-close btn-close-white" aria-label="Close"
      (click)="modal.dismiss('Cross click')"></button>
  </div>
  <div class="modal-body form-modal-body">
    <form #articleForm="ngForm">
      <div class="form-field-group">
        <label for="editArticleName" class="form-modal-label">
          <i class="bi bi-tag-fill"></i> Article Name <span class="required-star">*</span>
        </label>
        <input type="text" class="form-control form-modal-input" id="editArticleName"
          name="articleName" [(ngModel)]="modalContent!.articleName"
          placeholder="Enter article name" required />
      </div>
      <div class="form-field-group">
        <label for="editArticleType" class="form-modal-label">
          <i class="bi bi-layers-fill"></i> Type <span class="required-star">*</span>
        </label>
        <input type="text" class="form-control form-modal-input" id="editArticleType"
          name="type" [(ngModel)]="modalContent!.type"
          placeholder="Enter article type" required />
      </div>
      <div class="form-field-group">
        <label for="editArticleMaterial" class="form-modal-label">
          <i class="bi bi-gem"></i> Material
        </label>
        <select class="form-select form-modal-input" id="editArticleMaterial"
          name="material" [(ngModel)]="modalContent!.material">
          <option value="">Select Material</option>
          <option value="Aluminum">Aluminum</option>
          <option value="Steel">Steel</option>
          <option value="Composite">Composite</option>
        </select>
      </div>
    </form>
  </div>
  <div class="modal-footer detail-modal-footer">
    <button type="button" class="btn-modern-action btn-action-secondary"
      (click)="modal.dismiss('Cancel click')">
      <i class="bi bi-x-lg"></i> Cancel
    </button>
    <button type="button" class="btn-modern-action btn-action-primary"
      (click)="saveArticle(modalContent!)"
      [disabled]="!modalContent!.type || !modalContent!.articleName">
      <i class="bi bi-check-lg"></i> Update Article
    </button>
  </div>
</ng-template>
